<template>
  <div :class="b(modifiers)">
    <e-picture
      v-if="currentMediaType === 'image'"
      :class="b('media')"
      :srcset="currentImage.srcset"
      :sizes="sizes"
      :alt="currentImage.alt"
      :fallback="currentImage.fallback"
      :horizontal-anchor="data.imageHorizontalAnchor"
      :vertical-anchor="data.imageVerticalAnchor"
      :loading="currentImage.loading"
      placeholder
    />
    <video
      v-if="currentMediaType === 'video'"
      :class="b('media', { video: true })"
      :src="currentVideo.path"
      autoplay
      playsinline
      loop
      muted
    ></video>

    <div :class="b('overlay', { type: data.pictureOverlayVariant })"></div>

    <div :class="b('inner', { border: data.border })">
      <component
        :is="data.textVariant === 'title' ? 'h1' : 'div'"
        :class="b('text', textModifiers)"
      >
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div v-html="data.text"></div>
      </component>

      <div
        v-if="data.linkText && data.linkHref"
        :class="b('link-wrap')"
      >
        <e-button
          :class="b('link')"
          :href="data.linkHref"
          :target="data.linkTarget"
          spacing="600"
          :variant="data.pictureOverlayVariant === 'dark' ? 'default' : 'primary'"
          width="auto"
        >
          {{ data.linkText }}
        </e-button>
      </div>
    </div>
  </div>
</template>

<script>
  import eButton from '@/elements/e-button.vue';
  import ePicture from '@/elements/e-picture.vue';

  /**
   * Renders different variants of hero message.
   */
  export default {
    name: 'c-hero-message',
    components: {
      eButton,
      ePicture,
    },

    // mixins: [],

    props: {
      /**
       * Expects the components display data.
       *
       * @property {Object} data - The slide data.
       * @property {Boolean} data.border - Toggle gradiant border around content (default = false).
       * @property {Boolean} data.container - Toggle container around content (default = false).
       * @property {String} data.pictureOverlayVariant - The gradient overlay style. Accepts "light", "dark" or "regular" (default).
       * @property {String} data.text - The slide text.
       * @property {String} data.textVariant - The text style. Accepts "title" or "regular" (default).
       * @property {String} data.textShade - The background shade. Accepts "dark" or "light" (default).
       * @property {String} data.textColor - The text color. Accepts "blue" or "white" (default).
       * @property {String} data.linkText - The text of the button link.
       * @property {String} data.linkHref - The link of the button link.
       * @property {String} [data.linkTarget] - The target of the button link.
       * @property {String} data.mediaType - Type of media being displayed ("image", "video").
       * @property {String} data.image.src - The source of the item image.
       * @property {String} data.image.alt - The alt text for the item image.
       * @property {String} data.image.loading - The way the image should be loaded (lazy or eager).
       * @property {String} data.portraitImage.src - The source of the portrait item image.
       * @property {String} data.portraitImage.alt - The alt text for the portrait item image.
       * @property {String} data.portraitImage.loading - The way the image should be loaded (lazy or eager).
       * @property {Object} data.desktopVideo - Information about the desktop video.
       * @property {String} data.desktopVideo.path - Path to the desktop video.
       * @property {Object} data.tabletVideo - Information about the tablet video.
       * @property {String} data.tabletVideo.path - Path to the tablet video.
       * @property {Object} data.mobileVideo - Information about the mobile video.
       * @property {String} data.mobileVideo.path - Path to the mobile video.
       * @property {String} data.imageHorizontalAnchor - Orients the image horizontally to "left", "right" or "center" (default).
       * @property {String} data.imageVerticalAnchor - Orients the image vertically to "top", "bottom" or "center" (default).
       * TODO: image align v/h
       */
      data: {
        type: Object,
        required: true,
      },
    },
    // data() {
    //   return {};
    // },

    computed: {
      /**
       * Returns modifiers for the root element.
       *
       * @returns {Object}
       */
      modifiers() {
        return {
          container: this.data.container,
          mediaType: this.data.mediaType,
        };
      },

      /**
       * Defines the sizes for the image per viewport.
       *
       * @returns {Object}
       */
      sizes() {
        return {
          sm: 375,
          md: 768,
          lg: 1920,
        };
      },

      /**
       * Defines modifier flags for the text.
       *
       * @returns {Object}
       */
      textModifiers() {
        return {
          variant: this.data.textVariant || 'regular',
          shade: this.data.textShade || 'light',
          border: this.data.border,
          color: this.data.textColor || 'white',
        };
      },

      /**
       * Returns the image to display depending on current viewport size.
       *
       * @returns {Object}
       */
      currentImage() {
        return (this.$viewport.isMd ? this.data.image : this.data.portraitImage) || this.data.image;
      },

      /**
       * Returns the video to display based on the current viewport size.
       * If mobileVideo or tabletVideo is not available, defaults to desktopVideo.
       *
       * @returns {Object} The video object for the current viewport size or the desktop video object as default.
       */
      currentVideo() {
        let video;

        if (this.$viewport.isMobile) {
          video = this.finalMobileVideo;
        } else if (this.$viewport.isTablet) {
          video = this.finalTabletVideo;
        } else {
          video = this.data.desktopVideo;
        }

        return video;
      },

      /**
       * Determines the final video to be displayed on tablet, falling back to desktop if not available.
       * @type {Object}
       * @returns {Object} Video object for tablet.
       */
      finalTabletVideo() {
        return this.data.tabletVideo.path ? this.data.tabletVideo : this.data.desktopVideo;
      },

      /**
       * Determines the final video to be displayed on mobile, falling back to desktop if not available.
       * @type {Object}
       * @returns {Object} Video object for mobile.
       */
      finalMobileVideo() {
        return this.data.mobileVideo.path ? this.data.mobileVideo : this.data.desktopVideo;
      },

      /**
       * Returns the default for mediaType in case nothing is passed.
       * @returns {String}
       */
      currentMediaType() {
        return this.data?.mediaType ?? 'image';
      },
    },
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() { },
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    // methods: {},
    // render() {},
  };
</script>

<style lang="scss">
  @use 'sass:math';
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/functions';
  @use '@/setup/scss/extends' as *;

  .c-hero-message {
    $this: &;

    position: relative;
    height: functions.rem(600px);

    @include mixins.media(md) {
      height: functions.rem(500px);
    }

    &--container {
      margin: 0 functions.rem(variables.$spacing--15);

      @include mixins.media(md) {
        margin: 0 functions.rem(variables.$spacing--50);
      }

      @include mixins.media(lg) {
        width: functions.rem(1592px);
        margin: auto;
      }

      #{$this}__text--border {
        align-items: center;
      }
    }

    &--media-type-video {
      overflow: hidden;
    }

    &__inner {
      position: absolute;
      top: functions.rem(variables.$spacing--60);
      right: functions.rem(variables.$spacing--20);
      bottom: functions.rem(variables.$spacing--100);
      left: functions.rem(variables.$spacing--20);
      display: flex;
      flex-direction: column;
      justify-content: center;
      padding-right: functions.rem(variables.$spacing--60);

      @include mixins.media(sm) {
        top: functions.rem(variables.$spacing--60);
        right: functions.rem(variables.$spacing--40);
        bottom: functions.rem(variables.$spacing--130);
        left: functions.rem(variables.$spacing--40);
        padding-right: 30%;
      }

      @include mixins.media(md) {
        top: functions.rem(variables.$spacing--60);
        right: functions.rem(variables.$spacing--180);
        bottom: functions.rem(variables.$spacing--130);
        left: functions.rem(variables.$spacing--180);
        padding-right: 40%;
      }

      &--border {
        @extend %border-gradient;

        padding: functions.rem(variables.$spacing--40);

        @include mixins.media(sm) {
          padding: functions.rem(variables.$spacing--40 variables.$spacing--80);
        }
      }
    }

    &__overlay {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;

      @include mixins.media(sm) {
        right: 30%;
      }

      @include mixins.media(xl) {
        right: 50%;
      }

      &--type-light {
        background: linear-gradient(
          90deg,
          rgba(variables.$color-grayscale--1000, 80%) 45%,
          rgba(variables.$color-grayscale--1000, 0%) 95%
        );
      }

      &--type-dark {
        background: linear-gradient(
          90deg,
          rgba(variables.$color-grayscale--0, 80%) 45%,
          rgba(variables.$color-grayscale--0, 0%) 95%
        );
      }
    }

    &__media {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;

      &--video {
        @include mixins.z-index(back); // Fixes video indexing issue on Safari in a slider.

        width: 100%;
      }
    }

    &__link-wrap {
      flex: 0;
      margin-top: functions.rem(variables.$spacing--60);

      .e-button--variant-default {
        background: variables.$color-grayscale--1000;
      }
    }

    &__text {
      display: flex;
      flex: 1;
      align-items: center;
      margin-bottom: 0;
      color: variables.$color-primary--4;

      &--color-blue {
        color: variables.$color-primary--1;
      }

      &--border {
        align-items: flex-end;

        @include mixins.media(sm) {
          align-items: center;
        }
      }

      &--variant-regular {
        @include mixins.font-size(variables.$font-size--22);

        @include mixins.media(sm) {
          @include mixins.font-size(variables.$font-size--32);
        }
      }

      &--shade-dark {
        color: variables.$color-primary--1;
      }
    }
  }
</style>
